// ============================================
// Cloud Data Layer - Shared helpers + invalidation bus
// ============================================

import { supabase } from '../../lib/supabase';

// ---- Helper: get current user ID or throw ----
let cachedUserId: string | null = null;

// Listen for auth changes to invalidate cache
supabase.auth.onAuthStateChange((_event, session) => {
  cachedUserId = session?.user?.id ?? null;
});

export async function requireUserId(): Promise<string> {
  if (cachedUserId) return cachedUserId;
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user) throw new Error('Not authenticated');
  cachedUserId = session.user.id;
  return cachedUserId;
}

// ---- Helper: throw with a context-specific message ----
// PostgREST returns plain objects like { code, message, details, hint } that
// stringify as "[object Object]". Unwrap them to a useful message before
// rethrowing so the toast and Sentry payload have something meaningful.

// Fetch failures that never got an HTTP response. WebKit says "Load
// failed", Chromium "Failed to fetch", Firefox "NetworkError...", and
// iOS adds its own offline/timeout phrasings. Raw, they leak stack-trace
// jargon into UI headlines (a beginner reads "TypeError: Load failed"
// as a broken app - 2026-09-08 field report). One regex catches the
// family so every fetcher shows a calm message instead.
const NETWORK_FAILURE_RE = /load failed|failed to fetch|network ?error|internet connection appears to be offline|request timed out|network connection was lost/i;

export function isNetworkFailureMessage(message: string): boolean {
  return NETWORK_FAILURE_RE.test(message);
}

export const NETWORK_FAILURE_NAME = 'NetworkFailure';

/**
 * A sanitized network failure. `message` is the calm line the UI shows;
 * `technicalDetail` carries the original context + browser message for
 * telemetry, and `cause` keeps the original error for stack context.
 */
export interface NetworkFailureError extends Error {
  technicalDetail: string;
}

export function isNetworkFailureError(err: unknown): err is NetworkFailureError {
  return err instanceof Error && err.name === NETWORK_FAILURE_NAME;
}

export function throwWithContext(context: string, error: unknown): never {
  let message: string;
  if (error instanceof Error) {
    message = error.message;
  } else if (error && typeof error === 'object') {
    const e = error as { message?: unknown; details?: unknown; hint?: unknown; code?: unknown };
    const parts = [
      e.message ? String(e.message) : null,
      e.details ? String(e.details) : null,
      e.hint ? String(e.hint) : null,
      e.code ? `(${String(e.code)})` : null,
    ].filter(Boolean);
    message = parts.length > 0 ? parts.join(' ') : JSON.stringify(error);
  } else {
    message = String(error);
  }
  if (isNetworkFailureMessage(message)) {
    // Keep the technical detail for debugging; show the user a human one.
    const technicalDetail = `${context}: ${message}`;
    console.error(technicalDetail, error);
    const failure = new Error("Couldn't reach the server", { cause: error }) as NetworkFailureError;
    failure.name = NETWORK_FAILURE_NAME;
    failure.technicalDetail = technicalDetail;
    throw failure;
  }
  throw new Error(`${context}: ${message}`);
}

// ============================================
// Invalidation - simple pub/sub for refetching
// ============================================

export type Table = 'protocols' | 'dose_logs' | 'inventory' | 'reminders' | 'user_profiles' | 'peptides' | 'peptides_public' | 'peptide_stacks' | 'analyzed_videos' | 'user_saved_videos' | 'video_protocol_drafts' | 'educational_content' | 'faqs' | 'app_announcements';

type Listener = () => void;
const listeners = new Map<Table, Set<Listener>>();

export function subscribe(table: Table, fn: Listener): () => void {
  if (!listeners.has(table)) listeners.set(table, new Set());
  listeners.get(table)!.add(fn);
  return () => { listeners.get(table)?.delete(fn); };
}

export function notify(table: Table) {
  listeners.get(table)?.forEach((fn) => fn());
}
